// src/pages/BacktestVitrine.js
// Page Backtest — FM Replay : vidéo de présentation, 4 cartes d'action
// (téléchargement, guide, licence, statut) et tutoriel d'installation rapide.
// Design FLAT : bordures + aplats, aucune ombre d'élévation, remplit l'espace.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BookOpen,
  Check,
  CircleCheck,
  Cog,
  Copy,
  Crosshair,
  Download,
  MonitorDown,
  Info,
  KeyRound,
  LineChart,
  Lock,
  MonitorPlay,
  Play,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
// URL absolue : en relatif, CRA proxifie vers le `proxy` du package.json
// (127.0.0.1:5000) au lieu de REACT_APP_API_URL → ECONNREFUSED en dev.
import { API_ROOT } from "../api/api";
import i18n from "../i18n";
import PageHeader from "../components/shell/PageHeader";
import GUIDE_PDF from "../assets/pdf/Guide d'installation FM Replay.pdf";

/* --------------------------------- Données --------------------------------- */
const VIDEO_ID = "OQAGoQNnsZs";
const VIDEO_POSTER = `https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`;
const VIDEO_EMBED = `https://www.youtube.com/embed/${VIDEO_ID}?rel=0&modestbranding=1&playsinline=1&autoplay=1`;

// Fichier livré dans public/ : téléchargement direct, aucun appel backend.
const DOWNLOAD_URL = "/agents/FM_REPLAY.ex5";

// Nom du fichier tel qu'il arrive dans les téléchargements de l'utilisateur :
// webpack émet le PDF dans static/media avec un hash, illisible sans ça.
const GUIDE_FILENAME = "Guide d'installation FM Replay.pdf";

// WebRequest de vérification de licence — volontairement DIFFÉRENTE de celle
// de l'Agent IA (https://ia.fullmargin.net).
const LICENSE_WEBREQUEST = "https://secure.fullmargin.net/scripts/validate.php";

const HIGHLIGHTS = [
  { icon: Crosshair, key: "precision" },
  { icon: SlidersHorizontal, key: "replay" },
  { icon: MonitorPlay, key: "native" },
];

/* ---------------------------------- i18n ---------------------------------- */
const fr = {
  pageTitle: "Backtest",
  pageSubtitle: "FM Replay",
  hero: {
    title: "FM Replay — Le backtest nouvelle génération",
    text: "Un outil puissant pour rejouer et backtester n'importe quel marché directement dans MetaTrader 5, en mode tick par tick.",
    playAria: "Lire la vidéo de présentation FM Replay",
    videoTitle: "Présentation FM Replay",
  },
  highlights: {
    precision: {
      title: "Données tick par tick haute précision",
      text: "Une précision maximale pour des backtests fiables.",
    },
    replay: {
      title: "Relecture fluide et contrôles avancés",
      text: "Pilotez la vitesse, avancez pas à pas, analysez en détail.",
    },
    native: {
      title: "Intégré dans MetaTrader 5",
      text: "Installation simple et intégration native.",
    },
  },
  cards: {
    download: {
      title: "1. Télécharger l'outil",
      text: "Obtenez la dernière version de FM Replay pour MT5.",
      cta: "Télécharger maintenant",
      locked: "Abonnement requis",
    },
    guide: {
      title: "2. Guide d'installation",
      text: "Le guide complet en PDF, illustré pas à pas.",
      cta: "Télécharger le guide",
    },
    license: {
      title: "3. Licence FM Replay",
      text: "Votre licence générée automatiquement.",
      copy: "Copier la licence",
      copied: "Copiée !",
      loading: "Chargement…",
    },
    status: {
      title: "4. Statut de licence",
      active: "Licence active",
      inactive: "Licence inactive",
      activeText: "Votre licence est valide et prête à être utilisée.",
      inactiveText: "Activez votre abonnement FullMetrix pour générer votre licence.",
      activateCta: "Activer mon abonnement",
    },
  },
  reminder:
    "Rappel : votre licence FM Replay est générée automatiquement avec votre abonnement FullMetrix.",
  tutorial: {
    title: "Tutoriel d'installation rapide",
    prepare: {
      title: "1. Préparer MetaTrader 5",
      steps: [
        "Ouvrir MT5",
        "Aller dans « Outils > Options > Expert Advisors »",
        "Activer le trading algorithmique",
        "Cocher « Autoriser WebRequest pour les URL listées »",
        "Ajouter cette URL :",
      ],
    },
    install: {
      title: "2. Télécharger et installer FM Replay",
      steps: [
        "Télécharger l'outil depuis FullMetrix",
        "Installer FM Replay dans MetaTrader 5",
        "Ajouter votre licence dans les paramètres de l'outil",
        "Lancer l'outil sur un graphique",
        "Vérifier que la licence est active",
      ],
    },
  },
  important:
    "Important : ce WebRequest est utilisé pour la vérification de licence FM Replay. Il est différent du WebRequest de l'Agent IA.",
};

const en = {
  pageTitle: "Backtest",
  pageSubtitle: "FM Replay",
  hero: {
    title: "FM Replay — Next-generation backtesting",
    text: "A powerful tool to replay and backtest any market directly inside MetaTrader 5, tick by tick.",
    playAria: "Play the FM Replay presentation video",
    videoTitle: "FM Replay presentation",
  },
  highlights: {
    precision: { title: "High-precision tick data", text: "Maximum precision for reliable backtests." },
    replay: {
      title: "Smooth replay and advanced controls",
      text: "Control the speed, step forward, analyse in detail.",
    },
    native: { title: "Built into MetaTrader 5", text: "Simple installation and native integration." },
  },
  cards: {
    download: {
      title: "1. Download the tool",
      text: "Get the latest version of FM Replay for MT5.",
      cta: "Download now",
      locked: "Subscription required",
    },
    guide: {
      title: "2. Installation guide",
      text: "The complete guide as a PDF, illustrated step by step.",
      cta: "Download the guide",
    },
    license: {
      title: "3. FM Replay licence",
      text: "Your licence, generated automatically.",
      copy: "Copy licence",
      copied: "Copied!",
      loading: "Loading…",
    },
    status: {
      title: "4. Licence status",
      active: "Licence active",
      inactive: "Licence inactive",
      activeText: "Your licence is valid and ready to use.",
      inactiveText: "Activate your FullMetrix subscription to generate your licence.",
      activateCta: "Activate my subscription",
    },
  },
  reminder: "Reminder: your FM Replay licence is generated automatically with your FullMetrix subscription.",
  tutorial: {
    title: "Quick installation tutorial",
    prepare: {
      title: "1. Prepare MetaTrader 5",
      steps: [
        "Open MT5",
        "Go to “Tools > Options > Expert Advisors”",
        "Enable algorithmic trading",
        "Tick “Allow WebRequest for listed URL”",
        "Add this URL:",
      ],
    },
    install: {
      title: "2. Download and install FM Replay",
      steps: [
        "Download the tool from FullMetrix",
        "Install FM Replay in MetaTrader 5",
        "Add your license in the tool settings",
        "Launch the tool on a chart",
        "Check that the licence is active",
      ],
    },
  },
  important:
    "Important: this WebRequest is used for FM Replay licence verification. It is different from the AI Agent WebRequest.",
};

i18n.addResourceBundle("fr", "fmreplay", fr, true, true);
i18n.addResourceBundle("en", "fmreplay", en, true, true);

/* --------------------------------- Styles --------------------------------- */
const CARD = "rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.03]";
const ICONBOX = "grid place-items-center rounded-xl bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300";

/* ------------------------- Filigranes du tutoriel ------------------------- */
// Illustrations décoratives en fond de chaque bloc du tutoriel (comme la
// maquette) : engrenages pour la préparation de MT5, écran + téléchargement
// pour l'installation. Purement visuel, jamais lu par un lecteur d'écran.
function TutorialDecor({ variant }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute bottom-2 right-2 hidden text-violet-300/45 dark:text-violet-300/20 sm:block"
    >
      {variant === "prepare" ? (
        <div className="relative h-24 w-28">
          <Cog size={78} strokeWidth={1.3} className="absolute right-6 top-0" />
          <Cog size={46} strokeWidth={1.3} className="absolute bottom-0 right-0" />
        </div>
      ) : (
        <div className="relative h-24 w-28">
          <MonitorDown size={80} strokeWidth={1.2} className="absolute right-5 top-1" />
          <Cog size={34} strokeWidth={1.3} className="absolute bottom-1 right-0" />
        </div>
      )}
    </div>
  );
}

/* --------------------------------- Vidéo ---------------------------------- */
// Poster cliquable : l'iframe YouTube n'est montée qu'au clic (pas de script
// tiers chargé tant que l'utilisateur ne lance pas la vidéo).
function VideoCard({ t }) {
  const [playing, setPlaying] = useState(false);

  return (
    // h-full + aspect-auto en lg : la carte est étirée sur la hauteur de la
    // colonne de droite, sans ratio fixe la vidéo remplit tout (plus de vide).
    <div className="h-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 dark:border-white/10">
      <div className="relative aspect-video w-full lg:aspect-auto lg:h-full">
        {playing ? (
          <iframe
            src={VIDEO_EMBED}
            title={t("hero.videoTitle")}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={t("hero.playAria")}
            className="group absolute inset-0 h-full w-full"
          >
            <img
              src={VIDEO_POSTER}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="absolute inset-0 bg-black/25 transition group-hover:bg-black/15" />
            <span className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-violet-600/90 text-white ring-4 ring-white/25 transition group-hover:scale-105">
              <Play size={26} className="ml-1" fill="currentColor" />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------- Page ---------------------------------- */
export default function BacktestVitrine() {
  const { t } = useTranslation("fmreplay", { useSuspense: false });
  const [copied, setCopied] = useState(false);
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const token = (() => {
      try {
        return localStorage.getItem("jwt_token");
      } catch {
        return null;
      }
    })();
    fetch(`${API_ROOT}/fmmetrix/license`, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive) return;
        if (data && data.key) setLicense({ key: data.key, status: data.status || "active" });
        else setLicense(null);
      })
      .catch(() => alive && setLicense(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const hasLicense = !!license;
  const licenseKey = useMemo(
    () => (license ? license.key : loading ? t("cards.license.loading") : "XXXX-XXXX-XXXX-XXXX"),
    [license, loading, t]
  );

  const copyKey = async () => {
    if (!license) return;
    try {
      await navigator.clipboard.writeText(license.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 py-6 sm:px-6 sm:py-8">
      {/* TITRE — modèle commun (PageHeader). */}
      <PageHeader icon={LineChart} title={`${t("pageTitle")} — ${t("pageSubtitle")}`} subtitle={t("hero.text")} />

      {/* VIDÉO + PRÉSENTATION */}
      <section className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:items-stretch">
        <VideoCard t={t} />

        <div className={`${CARD} p-5 sm:p-6`}>
          <h2 className="text-[17px] font-bold text-violet-700 dark:text-violet-300">{t("hero.title")}</h2>
          <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600 dark:text-slate-300">{t("hero.text")}</p>

          <div className="mt-5 space-y-4">
            {/* Icône + titre en ligne, texte pleine largeur dessous (non indenté). */}
            {HIGHLIGHTS.map(({ icon: Icon, key }) => (
              <div key={key}>
                <div className="flex items-center gap-2.5">
                  <span className={`${ICONBOX} h-10 w-10 shrink-0`}>
                    <Icon size={18} />
                  </span>
                  <div className="min-w-0 text-[13.5px] font-bold text-slate-800 dark:text-white">
                    {t(`highlights.${key}.title`)}
                  </div>
                </div>
                <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500 dark:text-slate-400">
                  {t(`highlights.${key}.text`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 CARTES D'ACTION */}
      <section className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {/* 1 — Téléchargement */}
        <div className={`${CARD} flex flex-col p-5`}>
          <span className={`${ICONBOX} h-11 w-11`}>
            <Download size={20} />
          </span>
          <h3 className="mt-4 text-[14.5px] font-bold text-slate-800 dark:text-white">{t("cards.download.title")}</h3>
          <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500 dark:text-slate-400">
            {t("cards.download.text")}
          </p>

          {/* pt-4 : garde un écart net même quand le texte au-dessus tient sur
              plusieurs lignes et que mt-auto ne pousse plus rien. */}
          <div className="mt-auto pt-4">
            {hasLicense ? (
              <a
                href={DOWNLOAD_URL}
                download
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-violet-700"
              >
                {t("cards.download.cta")}
                <Download size={15} />
              </a>
            ) : (
              <>
                <span className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-[13px] font-semibold text-slate-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-500">
                  <Lock size={15} />
                  {t("cards.download.cta")}
                </span>
                <p className="mt-2 text-center text-[11px] text-slate-400 dark:text-slate-500">
                  {t("cards.download.locked")}
                </p>
              </>
            )}
          </div>
        </div>

        {/* 2 — Guide d'installation */}
        <div className={`${CARD} flex flex-col p-5`}>
          <span className={`${ICONBOX} h-11 w-11`}>
            <BookOpen size={20} />
          </span>
          <h3 className="mt-4 text-[14.5px] font-bold text-slate-800 dark:text-white">{t("cards.guide.title")}</h3>
          <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500 dark:text-slate-400">{t("cards.guide.text")}</p>

          <div className="mt-auto pt-4">
            <a
              href={GUIDE_PDF}
              download={GUIDE_FILENAME}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-violet-300 px-4 py-2.5 text-[13px] font-semibold text-violet-700 transition hover:bg-violet-50 dark:border-violet-400/40 dark:text-violet-300 dark:hover:bg-violet-500/10"
            >
              {t("cards.guide.cta")}
              <Download size={14} />
            </a>
          </div>
        </div>

        {/* 3 — Licence */}
        <div className={`${CARD} flex flex-col p-5`}>
          <span className={`${ICONBOX} h-11 w-11`}>
            <KeyRound size={20} />
          </span>
          <h3 className="mt-4 text-[14.5px] font-bold text-slate-800 dark:text-white">{t("cards.license.title")}</h3>
          <p className="mt-1 text-[12.5px] leading-relaxed text-slate-500 dark:text-slate-400">
            {t("cards.license.text")}
          </p>

          <div className="mt-auto pt-4">
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 dark:border-white/15 dark:bg-black/20">
              <span
                className={`flex-1 truncate font-mono text-[12.5px] font-semibold tracking-wider text-violet-700 dark:text-violet-300 ${
                  hasLicense ? "" : "select-none blur-[3px]"
                }`}
              >
                {licenseKey}
              </span>
              <button
                type="button"
                onClick={copyKey}
                disabled={!hasLicense}
                title={copied ? t("cards.license.copied") : t("cards.license.copy")}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-500 transition hover:bg-slate-200 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/10"
              >
                {copied ? <Check size={15} className="text-emerald-500" /> : <Copy size={15} />}
              </button>
            </div>
          </div>
        </div>

        {/* 4 — Statut */}
        <div className={`${CARD} flex flex-col p-5`}>
          <span
            className={`grid h-11 w-11 place-items-center rounded-xl ${
              hasLicense
                ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300"
                : "bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-slate-500"
            }`}
          >
            <ShieldCheck size={20} />
          </span>
          <h3 className="mt-4 text-[14.5px] font-bold text-slate-800 dark:text-white">{t("cards.status.title")}</h3>

          <div className="mt-3">
            {hasLicense ? (
              <span className="inline-flex items-center gap-2 rounded-lg bg-emerald-100 px-3 py-2 text-[13px] font-bold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                {t("cards.status.active")}
                <CircleCheck size={16} />
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-bold text-slate-500 dark:border-white/10 dark:text-slate-400">
                {t("cards.status.inactive")}
                <Lock size={15} />
              </span>
            )}
          </div>

          <p className="mt-3 text-[12.5px] leading-relaxed text-slate-500 dark:text-slate-400">
            {hasLicense ? t("cards.status.activeText") : t("cards.status.inactiveText")}
          </p>

          {!hasLicense && !loading ? (
            <div className="mt-auto pt-4">
              <a
                href="https://fullmargin.net/tarifs"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-violet-700"
              >
                {t("cards.status.activateCta")}
                <ArrowRight size={15} />
              </a>
            </div>
          ) : null}
        </div>
      </section>

      {/* RAPPEL LICENCE */}
      <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[12.5px] text-slate-600 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-300">
        <Info size={16} className="mt-0.5 shrink-0 text-slate-400" />
        <p>{t("reminder")}</p>
      </div>

      {/* TUTORIEL RAPIDE */}
      <section className={`${CARD} mt-4 p-5 sm:p-6`}>
        <div className="flex items-center gap-2.5">
          <BookOpen size={18} className="text-violet-600 dark:text-violet-300" />
          <h2 className="text-[15px] font-bold text-slate-800 dark:text-white">{t("tutorial.title")}</h2>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {["prepare", "install"].map((block, blockIdx) => {
            const steps = t(`tutorial.${block}.steps`, { returnObjects: true }) || [];
            return (
              <div
                key={block}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 p-4 dark:border-white/10 dark:bg-white/[0.02]"
              >
                <TutorialDecor variant={block} />

                <div className="relative flex items-center gap-2.5">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-violet-100 text-xs font-bold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                    {blockIdx + 1}
                  </span>
                  <h3 className="text-[13.5px] font-bold text-violet-700 dark:text-violet-300">
                    {t(`tutorial.${block}.title`)}
                  </h3>
                </div>

                <ul className="relative mt-3 space-y-2">
                  {(Array.isArray(steps) ? steps : []).map((step, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[12.5px] text-slate-600 dark:text-slate-300">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-400" />
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>

                {/* L'URL de licence est affichée telle quelle, copiable. */}
                {block === "prepare" ? (
                  <div className="relative mt-2.5 flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 dark:border-white/15 dark:bg-black/20">
                    <code className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-violet-700 dark:text-violet-300">
                      {LICENSE_WEBREQUEST}
                    </code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(LICENSE_WEBREQUEST)}
                      title={t("cards.license.copy")}
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-500 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      {/* AVERTISSEMENT WEBREQUEST */}
      <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-amber-300/50 bg-amber-50 px-4 py-3 text-[12.5px] text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-200">
        <Info size={16} className="mt-0.5 shrink-0" />
        <p>{t("important")}</p>
      </div>
    </div>
  );
}
